import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import _ from 'lodash';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';

// Material-UI
import {
  Box,
  Card,
  Container,
  Typography,
  Paper,
  Button,
  Chip,
  Divider,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Fade,
  alpha,
  useTheme,
  Stack,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  InputAdornment,
  Collapse,
  Badge,
  Tab,
  Tabs,
  Menu,
  Select,
  FormControl,
  InputLabel,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';

// Icons
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import DateRangeIcon from '@mui/icons-material/DateRange';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import TodayIcon from '@mui/icons-material/Today';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GroupIcon from '@mui/icons-material/Group';
import SaveIcon from '@mui/icons-material/Save';
import Loading from 'components/Loading';
import {
  timestampReportPersonal,
  timeStampMontlyReport,
} from '../../components/PDF';
import FilterTimestampReportCard from '../../components/Card/FilterTimestampReportCard';
import AttendanceRulesPanel from '../../components/Card/AttendanceRulesPanel';
import * as actions from '../../redux/actions';
import TimestampProjectCost from '../../components/Table/TimestampProjectCost';
import MergedPayrollTable from '../../components/Table/MergedPayrollTable';

dayjs.extend(weekday);
dayjs.extend(localeData);

export default function HrReport({ title, subtitle }) {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();
  const employees = useSelector((state) => state.employee);
  const timestamp = useSelector((state) => state.timestamp);
  const timestampPayroll = useSelector((state) => state.timestampPayroll);
  const roletype = useSelector((state) => state.roletype);
  const project = useSelector((state) => state.project);
  const me = useSelector((state) => state.me);
  const info = useSelector((state) => state.info);
  const manualWork = useSelector((state) => state.manualWork);
  const leaveRequest = useSelector((state) => state.leaveRequest);
  const payrollAdj = useSelector((state) => state.payrollAdj);

  // stabilize array references — กัน child re-render เมื่อ parent render โดยไม่จำเป็น
  const manualRows = useMemo(() => manualWork?.rows || [], [manualWork]);
  const leaveRows = useMemo(() => leaveRequest?.rows || [], [leaveRequest]);

  const [name, setName] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(9999);
  const [emSelect, setEmSelect] = useState();
  const [dateStart, setDateStart] = useState(dayjs().startOf('month'));
  const [dateEnd, setDateEnd] = useState(dayjs().endOf('month'));
  const [month, setMonth] = useState(dayjs().month());
  const [year, setYear] = useState(dayjs().year());
  const [roleSelect, setRoleSelect] = useState();
  const [projectSelect, setProjectSelect] = useState();
  const [typeSelect, setTypeSelect] = useState('DAILY');
  const [selectedOption, setSelectedOption] = useState(
    // DAILY default → ครึ่งเดือนของวันปัจจุบัน | FULLTIME default → ทั้งเดือน
    dayjs().date() <= 15 ? '15' : '31',
  );
  const [countDay, setCountDay] = useState(
    dayjs(`${year}-${month + 1}`).daysInMonth(),
  );
  const [startDay, setStartDay] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  // salary form state
  const [note, setNote] = useState('');
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectEachEmployee, setSelectEachEmployee] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: { salaryData: [] } });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'salaryData',
  });

  // ยอดรวมประกันสังคม (คอลัมน์รายได้.ประกันสังคม รวมทุกคน) — ใช้กระจายเป็น % ค่าแรงต่อโครงการใน TimestampProjectCost
  const watchedSalaryData = useWatch({ control, name: 'salaryData' }) || [];
  const totalSsoAmount = useMemo(
    () => (fields || []).reduce((sum, f, i) => {
      const w = watchedSalaryData[i] || {};
      return sum + parseFloat(w?.expenses?.sso ?? f?.expenses?.sso ?? 0);
    }, 0),
    [fields, watchedSalaryData],
  );

  const daysInMonth = dayjs(`${year}-${month + 1}`).daysInMonth();
  const midDay = Math.ceil(daysInMonth / 2);
  const days = useMemo(() => Array.from({ length: countDay }, (_, i) => i + startDay), [countDay, startDay]);
  const attendanceRules = useMemo(() => info?.setting?.payroll?.attendance_rules || [], [info]);
  const monthNames = [
    'มกราคม',
    'กุมภาพันธ์',
    'มีนาคม',
    'เมษายน',
    'พฤษภาคม',
    'มิถุนายน',
    'กรกฎาคม',
    'สิงหาคม',
    'กันยายน',
    'ตุลาคม',
    'พฤศจิกายน',
    'ธันวาคม',
  ];

  // sync form fields เมื่อ payroll data หรือ adjustments เปลี่ยน
  // adj (backend-persisted) override ค่า default จาก backend
  useEffect(() => {
    const adjByEmpId = {};
    (payrollAdj?.rows || []).forEach((a) => { adjByEmpId[String(a.employee_id)] = a; });
    const format2 = (val) => val != null && val !== '' ? Number(val).toFixed(2) : val;

    const newData = (timestampPayroll?.rows || []).map((row) => {
      const adj = adjByEmpId[String(row.employee_id)] || {};
      const mf = adj.manual_fields || [];
      return {
        ...row,
        manual_fields: mf,
        revenue: {
          ...row.revenue,
          salary: format2(mf.includes('salary') ? (adj.revenue?.salary ?? row.revenue?.salary) : row.revenue?.salary),
          allowances: format2(mf.includes('allowances') ? (adj.revenue?.allowances ?? row.revenue?.allowances) : row.revenue?.allowances),
          bonus: format2(mf.includes('bonus') ? (adj.revenue?.bonus ?? row.revenue?.bonus) : row.revenue?.bonus),
          overtime: format2(mf.includes('overtime') ? (adj.revenue?.overtime ?? row.revenue?.overtime) : row.revenue?.overtime),
          field_allowance: format2(mf.includes('field_allowance') ? (adj.revenue?.field_allowance ?? row.revenue?.field_allowance) : row.revenue?.field_allowance),
        },
        expenses: {
          ...row.expenses,
          tax: format2(mf.includes('tax') ? (adj.expenses?.tax ?? row.expenses?.tax) : row.expenses?.tax),
          other: format2(mf.includes('other') ? (adj.expenses?.other ?? row.expenses?.other) : row.expenses?.other),
          sso: format2(mf.includes('sso') ? (adj.expenses?.sso ?? row.expenses?.sso) : row.expenses?.sso),
          late: format2(mf.includes('late') ? (adj.expenses?.late ?? row.expenses?.late) : row.expenses?.late),
        },
        note: adj.note ?? row.note,
      };
    });
    const t = setTimeout(() => setValue('salaryData', newData), 0);
    return () => clearTimeout(t);
  }, [timestampPayroll, payrollAdj]); // eslint-disable-line

  // fetch ทั้งสองตาราง + manualWork + leave requests + payroll adjustments พร้อมกัน
  const refreshAll = useCallback((start, end, type, projId) => {
    Promise.all([
      dispatch(actions.timestampPayrollDashboard({
        dateStart: start, dateEnd: end, typeSelect: type, project_id: projId,
      })),
      dispatch(actions.timestampPayrollFetch({
        dateStart: start, dateEnd: end, typeSelect: type,
      })),
      dispatch(actions.manualWorkAll({ dateStart: start, dateEnd: end })),
      dispatch(actions.leaveRequestAll({ status: 'APPROVED' })),
      dispatch(actions.payrollAdjAll({ dateStart: start, dateEnd: end })),
    ]);
  }, [dispatch]);

  const fetchTimerRef = useRef(null);

  useEffect(() => {
    let start; let end;
    // .toISOString() ไม่ใช่ .format('...[Z]') — [Z] เดิมเป็นตัวอักษรแปะต่อท้ายเฉยๆ ไม่ได้แปลงเวลาไทย (ของ
    // dayjs() ในเบราว์เซอร์) เป็น UTC จริง ทำให้ปลายทางที่ new Date(...)/dayjs(...) ค่านี้ตรงๆ (backend query
    // /timestamp*, /leave-request, /manual-work-entry) ตีความผิดเป็น UTC จริง คลาดไป 7 ชม. (ดู HrReportV2.js
    // computeRange ที่แก้จุดเดียวกันนี้ไปแล้ว)
    if (selectedOption === 'Month') {
      start = dayjs()
        .year(year)
        .month(month)
        .startOf('month')
        .toISOString();
      end = dayjs()
        .year(year)
        .month(month)
        .endOf('month')
        .toISOString();
      setCountDay(dayjs(`${year}-${month + 1}`).daysInMonth());
      setStartDay(1);
    } else if (selectedOption === '15') {
      start = dayjs()
        .year(year)
        .month(month)
        .startOf('month')
        .toISOString();
      end = dayjs()
        .year(year)
        .month(month)
        .date(15)
        .endOf('day')
        .toISOString();
      setCountDay(15);
      setStartDay(1);
    } else if (selectedOption === '31') {
      start = dayjs()
        .year(year)
        .month(month)
        .date(16)
        .startOf('day')
        .toISOString();
      end = dayjs()
        .year(year)
        .month(month)
        .endOf('month')
        .toISOString();
      setCountDay(dayjs(`${year}-${month + 1}`).daysInMonth() - 15);
      setStartDay(16);
    }
    setDateStart(start);
    setDateEnd(end);

    // debounce 250ms — ป้องกัน rapid re-fetch เมื่อ state หลายตัวเปลี่ยนพร้อมกัน
    clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(() => {
      refreshAll(start, end, typeSelect, projectSelect?._id);
    }, 250);

    return () => clearTimeout(fetchTimerRef.current);
    // eslint-disable-next-line
  }, [emSelect, month, year, roleSelect, projectSelect, selectedOption, typeSelect]);

  useEffect(() => {
    dispatch(actions.employeeAll({}));
    dispatch(actions.roletypeAll({}));
    dispatch(actions.projectAll({}));
    dispatch(actions.getInformation({}));
    // eslint-disable-next-line
  }, []);

  const handlePrint = () => {
    timeStampMontlyReport(info, timestamp, days, month, year, dateStart, leaveRequest?.rows || []);
  };

  const handleValueChange = (event) => {
    const count = event.target.value;
    setSelectedOption(count);
  };

  const clearFilters = () => {
    setEmSelect(undefined);
    setProjectSelect(undefined);
    setRoleSelect(undefined);
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handlePreviousMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  // บันทึก allowances/bonus/tax/other/note ของพนักงาน 1 คนลง backend ทันที
  const onAdjSave = useCallback((empId) => {
    const fi = fields.findIndex((f) => String(f.employee_id) === String(empId));
    if (fi === -1) return;
    const row = getValues(`salaryData.${fi}`);
    const toNum = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n; };
    dispatch(actions.payrollAdjUpsert({
      employee_id: String(empId),
      dateStart,
      dateEnd,
      revenue: {
        allowances: toNum(row?.revenue?.allowances),
        bonus: toNum(row?.revenue?.bonus),
        overtime: toNum(row?.revenue?.overtime),
        field_allowance: toNum(row?.revenue?.field_allowance),
      },
      expenses: {
        tax: toNum(row?.expenses?.tax),
        other: toNum(row?.expenses?.other),
        sso: toNum(row?.expenses?.sso),
        late: toNum(row?.expenses?.late),
      },
      note: row?.note?.trim() || null,
      manual_fields: row?.manual_fields || [],
    }));
  }, [fields, getValues, dateStart, dateEnd, dispatch]);

  const handleNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const handleChangeMonth = (event) => {
    setMonth(parseInt(event.target.value));
  };

  const handleChangeYear = (event) => {
    setYear(parseInt(event.target.value));
  };

  const handleOpenModal = useCallback((data) => {
    setSelectEachEmployee(data);
    setOpenModal(true);
  }, []);

  const onSalaryOverride = useCallback(async (id, payload) => {
    await dispatch(actions.timestampSalaryOverride(id, payload));
  }, [dispatch]);

  const onSkipLateDeduct = useCallback((id, payload) => dispatch(actions.timestampSkipLate(id, payload)), [dispatch]);

  const onCheckInTimeUpdate = useCallback((id, payload) => dispatch(actions.timestampUpdateCheckInTime(id, payload)), [dispatch]);
  const onProjectUpdate = useCallback((id, payload) => dispatch(actions.timestampUpdateProject(id, payload)), [dispatch]);
  const onCheckInTimeClear = useCallback((id) => dispatch(actions.timestampClearTimeOverride(id)), [dispatch]);
  const onProjectClear = useCallback((id) => dispatch(actions.timestampClearProjectOverride(id)), [dispatch]);
  const onHourOverride = useCallback((id, payload) => dispatch(actions.timestampHourOverride(id, payload)), [dispatch]);
  const onOtOverride = useCallback((id, payload) => dispatch(actions.timestampToggleOtOverride(id, payload)), [dispatch]);

  const [isTableLoading, setIsTableLoading] = useState(false);
  const [tableDataVersion, setTableDataVersion] = useState(0);
  const [showFormulas, setShowFormulas] = useState(false);
  const [salaryMode, setSalaryMode] = useState('work-time');

  // partial refresh — เรียกแค่ 2 timestamp endpoints (ไม่ดึง manualWork/leave/payrollAdj ที่ไม่เปลี่ยน)
  const refreshTimestampOnly = useCallback(async () => {
    setIsTableLoading(true);
    try {
      await Promise.all([
        dispatch(actions.timestampPayrollDashboard({
          dateStart, dateEnd, typeSelect, project_id: projectSelect?._id,
        })),
        dispatch(actions.timestampPayrollFetch({ dateStart, dateEnd, typeSelect })),
      ]);
      // รอ setTimeout ใน setValue useEffect วิ่งก่อน ถึงจะซ่อน overlay
      await new Promise((r) => setTimeout(r, 80));
      setTableDataVersion((v) => v + 1);
    } finally {
      setIsTableLoading(false);
    }
  }, [dispatch, dateStart, dateEnd, typeSelect, projectSelect]);

  const onManualSave = useCallback(
    (payload) => dispatch(actions.manualWorkUpsert(payload))
      .then(() => refreshAll(dateStart, dateEnd, typeSelect, projectSelect?._id)),
    [dispatch, refreshAll, dateStart, dateEnd, typeSelect, projectSelect],
  );

  const onManualDelete = useCallback(
    (id) => dispatch(actions.manualWorkDelete(id))
      .then(() => refreshAll(dateStart, dateEnd, typeSelect, projectSelect?._id)),
    [dispatch, refreshAll, dateStart, dateEnd, typeSelect, projectSelect],
  );

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectEachEmployee('');
  };

  const onSubmit = async (data) => {
    const total_tax = data.salaryData.reduce(
      (sum, item) => sum + parseFloat(item?.expenses?.tax || 0), 0,
    );
    const total_sso = data.salaryData.reduce(
      (sum, item) => sum + parseFloat(item?.expenses?.sso || 0), 0,
    );
    const isWorkTime = salaryMode === 'work-time';
    const total_late = isWorkTime ? 0 : data.salaryData.reduce(
      (sum, item) => sum + parseFloat(item?.expenses?.late || 0), 0,
    );
    const totalNet = data.salaryData.reduce((sum, item) => {
      const salary = parseFloat(item?.revenue?.salary || 0);
      const overtime = parseFloat(item?.revenue?.overtime || 0);
      const allowances = parseFloat(item?.revenue?.allowances || 0);
      const bonus = parseFloat(item?.revenue?.bonus || 0);
      const tax = parseFloat(item?.expenses?.tax || 0);
      const sso = parseFloat(item?.expenses?.sso || 0);
      const late = isWorkTime ? 0 : parseFloat(item?.expenses?.late || 0);
      const other = parseFloat(item?.expenses?.other || 0);
      const field_allowance = parseFloat(item?.revenue?.field_allowance || 0);
      const itemTotal = typeSelect === 'FULLTIME'
        ? salary + overtime + allowances + bonus + field_allowance - tax - sso - late - other
        : salary + overtime + allowances + bonus + field_allowance - tax - sso - late - other;
      return sum + itemTotal;
    }, 0);

    const dataSubmit = {
      dateStart,
      dateEnd,
      role_type: typeSelect,
      total_tax,
      total_sso,
      total_late,
      total: totalNet,
      timestampList: [...data?.salaryData?.map((e) => e.checkins).flat()],
      salaryData: data.salaryData.map((eachdata) => ({
        ...eachdata,
        employee: eachdata?.employee_id,
        payrollType: typeSelect,
        sso_percentage: eachdata?.sso,
        expenses: {
          ...eachdata?.expenses,
          late: isWorkTime ? 0 : parseFloat(eachdata?.expenses?.late || 0),
        },
        total: typeSelect === 'FULLTIME'
          ? parseFloat(eachdata?.revenue?.salary || 0)
            + parseFloat(eachdata?.revenue?.overtime || 0)
            + parseFloat(eachdata?.revenue?.allowances || 0)
            + parseFloat(eachdata?.revenue?.bonus || 0)
            + parseFloat(eachdata?.revenue?.field_allowance || 0)
            - parseFloat(eachdata?.expenses?.other || 0)
            - parseFloat(eachdata?.expenses?.tax || 0)
            - parseFloat(eachdata?.expenses?.sso || 0)
            - (isWorkTime ? 0 : parseFloat(eachdata?.expenses?.late || 0))
          : parseFloat(eachdata?.revenue?.salary || 0)
            + parseFloat(eachdata?.revenue?.overtime || 0)
            + parseFloat(eachdata?.revenue?.allowances || 0)
            + parseFloat(eachdata?.revenue?.bonus || 0)
            + parseFloat(eachdata?.revenue?.field_allowance || 0)
            - parseFloat(eachdata?.expenses?.tax || 0)
            - parseFloat(eachdata?.expenses?.sso || 0)
            - (isWorkTime ? 0 : parseFloat(eachdata?.expenses?.late || 0))
            - parseFloat(eachdata?.expenses?.other || 0),
      })),
      dashboard: timestampPayroll?.dashboard || [],
      note,
    };

    setSalaryLoading(true);
    try {
      await dispatch(actions.salaryListCreate(dataSubmit));
      dispatch(actions.salaryListAll({}));
      history.push('/humen/salarylist');
    } finally {
      setSalaryLoading(false);
    }
  };

  // Render compact header with month selection
  const renderCompactHeader = () => (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={month}
            onChange={handleChangeMonth}
            displayEmpty
            variant="outlined"
            sx={{ height: 36 }}
          >
            {monthNames.map((n, index) => (
              <MenuItem key={index} value={index}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={year}
            onChange={handleChangeYear}
            displayEmpty
            variant="outlined"
            sx={{ height: 36 }}
          >
            {Array.from(
              { length: 10 },
              (_, i) => new Date().getFullYear() - 4 + i,
            ).map((yearOption) => (
              <MenuItem key={yearOption} value={yearOption}>
                {yearOption}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={selectedOption}
          exclusive
          onChange={(e, value) =>
            value && handleValueChange({ target: { value } })
          }
          size="small"
        >
          <ToggleButton value="Month" sx={{ py: 0.5, px: 1 }}>
            ทั้งเดือน
          </ToggleButton>
          <ToggleButton value="15" sx={{ py: 0.5, px: 1 }}>
            1-15
          </ToggleButton>
          <ToggleButton value="31" sx={{ py: 0.5, px: 1 }}>
            16-{daysInMonth}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {(emSelect || projectSelect || roleSelect || selectedOption !== 'Month') && (
          <Chip
            icon={<FilterAltIcon fontSize="small" />}
            label={`ตัวกรอง ${
              [emSelect, projectSelect, roleSelect].filter(Boolean).length +
              (selectedOption !== 'Month' ? 1 : 0)
            }`}
            size="small"
            variant="outlined"
            onClick={() => setShowFilters(!showFilters)}
            deleteIcon={showFilters ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />}
            onDelete={() => setShowFilters(!showFilters)}
          />
        )}

        {me?.userData?.permissions?.hr_management && (
          <Button
            variant="contained"
            size="small"
            onClick={handlePrint}
            startIcon={<PrintIcon />}
          >
            พิมพ์
          </Button>
        )}

      </Box>
    </Box>
  );
  const renderReportContent = () => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          p: 1,
          bgcolor: alpha(theme.palette.background.default, 0.5),
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="subtitle1" fontWeight={500}>
          รายงานข้อมูล
          {selectedOption === 'Month'
            ? 'ทั้งเดือน'
            : selectedOption === '15'
            ? ' 1-15'
            : ` 16-${daysInMonth}`}{' '}
          {monthNames[month]} {year}
        </Typography>

        <Box>
          <IconButton size="small" onClick={handlePreviousMonth}>
            <KeyboardArrowLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setMonth(dayjs().month());
              setYear(dayjs().year());
            }}
          >
            <TodayIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleNextMonth}>
            <KeyboardArrowRightIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box>
          <FormControl required fullWidth size="small">
            <Select
              onChange={(event) => {
                const newType = event.target.value;
                setTypeSelect(newType);
                if (newType === 'FULLTIME') {
                  setSelectedOption('Month');
                } else {
                  setSelectedOption(dayjs().date() <= 15 ? '15' : '31');
                }
              }}
              value={typeSelect}
              displayEmpty
              sx={{
                borderRadius: 1.5,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <MenuItem value="FULLTIME">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                  <Typography>พนักงานรายเดือน</Typography>
                </Stack>
              </MenuItem>
              <MenuItem value="DAILY">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                  <Typography>พนักงานรายวัน</Typography>
                </Stack>
              </MenuItem>
              <MenuItem value="">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                  <Typography>ทั้งหมด</Typography>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {me?.userData?.permissions?.hr_management && (
        <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <AttendanceRulesPanel
            info={info}
            showFormulas={showFormulas}
            onShowFormulasChange={setShowFormulas}
            salaryMode={salaryMode}
            onSalaryModeChange={setSalaryMode}
            onSave={(payload) => {
              dispatch(actions.editOneInformation(info?._id, payload))
                .then(() => dispatch(actions.getInformation({})))
                .then(() => Promise.all([
                  dispatch(actions.timestampPayrollDashboard({
                    dateStart,
                    dateEnd,
                    typeSelect,
                    project_id: projectSelect?._id,
                  })),
                  dispatch(actions.timestampPayrollFetch({
                    dateStart,
                    dateEnd,
                    typeSelect,
                  })),
                ]));
            }}
          />
        </Box>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Fade in={true} timeout={500}>
          <Box>
            <MergedPayrollTable
              timestamp={timestamp}
              month={month}
              year={year}
              daysInMonth={daysInMonth}
              days={days}
              selectedOption={selectedOption}
              permission={me?.userData?.permissions}
              me={me?.userData}
              onSalaryOverride={onSalaryOverride}
              onSkipLateDeduct={onSkipLateDeduct}
              onCheckInTimeUpdate={onCheckInTimeUpdate}
              onCheckInTimeClear={onCheckInTimeClear}
              onProjectUpdate={onProjectUpdate}
              onProjectClear={onProjectClear}
              onHourOverride={onHourOverride}
              onOtOverride={onOtOverride}
              projectList={project?.rows || []}
              onRefreshTimestamp={refreshTimestampOnly}
              isLoading={isTableLoading}
              dataVersion={tableDataVersion}
              manualWorkData={manualRows}
              leaveData={leaveRows}
              onManualSave={onManualSave}
              onManualDelete={onManualDelete}
              fields={fields}
              control={control}
              getValues={getValues}
              setValue={setValue}
              remove={remove}
              onOpenCheckinModal={handleOpenModal}
              typeSelect={typeSelect}
              attendanceRules={attendanceRules}
              onAdjSave={onAdjSave}
              employees={employees?.rows || []}
              showFormulas={showFormulas}
              salaryMode={salaryMode}
            />
          </Box>
        </Fade>

        {me?.userData?.permissions?.hr_management && (
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <TextField
              label="หมายเหตุ"
              multiline
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              fullWidth
              size="small"
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={salaryLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                disabled={salaryLoading || fields.length === 0}
              >
                {salaryLoading ? 'กำลังบันทึก...' : 'บันทึกเงินเดือน'}
              </Button>
            </Box>
          </Box>
        )}
      </form>
    </Paper>
  );

  const renderProjectCost = () => (
    <div>
      <TimestampProjectCost timestamp={timestamp} totalSsoAmount={totalSsoAmount} />
    </div>
  );

  const LEAVE_TYPE_LABEL = {
    SICK: 'ลาป่วย',
    PERSONAL: 'ลากิจ',
    VACATION: 'ลาพักร้อน',
    MATERNITY: 'ลาคลอด',
    ORDINATION: 'ลาบวช',
    UNPAID: 'ลาไม่รับค่าจ้าง',
  };
  const LEAVE_COLOR_CHIP = {
    SICK: '#ec4899',
    PERSONAL: '#7c3aed',
    VACATION: '#059669',
    MATERNITY: '#d97706',
    ORDINATION: '#ea580c',
    UNPAID: '#64748b',
  };

  const renderLeaveSummary = () => {
    const monthStart = dayjs(`${year}-${month + 1}`).startOf('month');
    const monthEnd = dayjs(`${year}-${month + 1}`).endOf('month');

    const monthLeaves = (leaveRequest?.rows || []).filter((lr) => {
      const s = dayjs(lr.start_date);
      const e = dayjs(lr.end_date);
      return s.isBefore(monthEnd) && e.isAfter(monthStart);
    });

    if (!monthLeaves.length) return null;

    // แตกแต่ละ leave ออกเป็นรายวันในเดือนนั้น
    const leaveRows = [];
    monthLeaves.forEach((lr) => {
      const empRow = (timestamp?.rows || []).find(
        (r) => String(r.employee_id) === String(lr.employee?._id || lr.employee),
      );
      const empName = empRow?.name || `${lr.employee?.firstname || ''} ${lr.employee?.lastname || ''}`.trim() || 'ไม่ระบุ';

      let d = dayjs(lr.start_date).startOf('day');
      const lEnd = dayjs(lr.end_date).startOf('day');
      while (!d.isAfter(lEnd)) {
        if (d.month() === month && d.year() === year) {
          const dayNum = d.date();
          const checkin = empRow?.checkins?.find(
            (c) => dayjs(c?.checkIn).format('D/M/YY') === `${dayNum}/${month + 1}/${year % 100}`,
          );
          const checkinHour = checkin ? dayjs(checkin.checkIn).hour() : null;
          const isHalf = lr.period !== 'FULL_DAY';
          const nonLeavePeriod = lr.period === 'MORNING' ? 'ช่วงบ่าย' : lr.period === 'AFTERNOON' ? 'ช่วงเช้า' : null;
          const isCheckinInLeavePeriod = isHalf && checkin != null
            ? (lr.period === 'MORNING' ? checkinHour < 12 : checkinHour >= 12)
            : null;

          leaveRows.push({ lr, empName, day: d, checkin, isHalf, nonLeavePeriod, isCheckinInLeavePeriod });
        }
        d = d.add(1, 'day');
      }
    });

    return (
      <Paper elevation={0} sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            สรุปการลา — {monthNames[month]} {year}
          </Typography>
          <Chip size="small" label={`${leaveRows.length} รายการ`} variant="outlined" />
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>พนักงาน</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>ประเภท</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>ช่วงเวลา</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>วันที่</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>ลงเวลาช่วงที่เหลือ</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>เวลาเข้า</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>สถานะ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaveRows.map(({ lr, empName, day, checkin, isHalf, nonLeavePeriod, isCheckinInLeavePeriod }, idx) => {
              let statusLabel = '';
              let statusColor = 'default';
              if (!isHalf) {
                statusLabel = checkin ? 'มีการลงเวลา (ตรวจสอบ)' : 'ลาเต็มวัน';
                statusColor = checkin ? 'warning' : 'default';
              } else if (!checkin) {
                statusLabel = `ไม่มีการลงเวลา${nonLeavePeriod}`;
                statusColor = 'warning';
              } else if (isCheckinInLeavePeriod) {
                statusLabel = 'ลงเวลาในช่วงลา ⚠';
                statusColor = 'error';
              } else {
                statusLabel = `ลงเวลา${nonLeavePeriod} ✓`;
                statusColor = 'success';
              }

              return (
                <TableRow key={idx} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{empName}</TableCell>
                  <TableCell>
                    <Box sx={{
                      display: 'inline-block',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: LEAVE_COLOR_CHIP[lr.leave_type] || '#64748b',
                      border: `1px solid ${LEAVE_COLOR_CHIP[lr.leave_type] || '#64748b'}`,
                    }}>
                      {LEAVE_TYPE_LABEL[lr.leave_type] || lr.leave_type}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem' }}>
                    {lr.period === 'FULL_DAY' ? 'เต็มวัน' : lr.period === 'MORNING' ? 'ครึ่งวันเช้า' : 'ครึ่งวันบ่าย'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                    {day.format('DD/MM/YY')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.78rem' }}>
                    {!isHalf ? '—' : checkin ? (
                      <Typography variant="caption" sx={{ color: isCheckinInLeavePeriod ? 'error.main' : 'success.main', fontWeight: 700 }}>
                        {isCheckinInLeavePeriod ? 'ในช่วงลา ⚠' : `${nonLeavePeriod} ✓`}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 700 }}>
                        ไม่มี ✗
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem' }}>
                    {checkin?.checkInTime || (isHalf ? '—' : '-')}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={statusLabel} color={statusColor} variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 22 }} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  const renderModal = () => (
    <Modal open={openModal} onClose={handleCloseModal}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          maxHeight: '70vh',
          overflowY: 'auto',
          minWidth: 400,
        }}
      >
        <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
          <Button onClick={handleCloseModal} sx={{ minWidth: 0, p: 0.5, borderRadius: '50%' }}>
            <span style={{ fontSize: 22, fontWeight: 700 }}>&times;</span>
          </Button>
        </Box>
        <Typography variant="h6" gutterBottom>รายการลงเวลา</Typography>
        <Table size="small" sx={{ minWidth: 400 }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 600 }}>วันที่</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>โครงการ</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>เข้า</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>ออก</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>วัน</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>หมายเหตุ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {_.map(selectEachEmployee, (e, idx) => (
              <TableRow key={idx}>
                <TableCell align="center">{dayjs(e?.checkIn).format('DD/MM/YYYY')}</TableCell>
                <TableCell align="center">{e?.project_number || '-'} {e?.project_name || '-'}</TableCell>
                <TableCell align="center">{e?.checkInTime || '-'}</TableCell>
                <TableCell align="center">{e?.checkOutTime || '-'}</TableCell>
                <TableCell align="center">{e?.totalTime || '-'}</TableCell>
                <TableCell align="center" sx={{ color: e?.note ? 'warning.main' : 'text.secondary' }}>
                  {e?.note || '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Modal>
  );

  if (timestamp.loading || !timestamp.isCompleted) {
    return <Loading isLoading />;
  }
  return (
    <div>
      {renderCompactHeader()}
      {renderReportContent()}
      {renderLeaveSummary()}
      {renderProjectCost()}
      {renderModal()}
    </div>
  );
}
